export interface IRouteGraphSegment {
  startStopId: number;
  endStopId: number;
  weightSeconds: number;
}

export interface IRouteGraphRequest {
  startStopId: number;
  endStopId: number | null;
  segments: IRouteGraphSegment[];
  // Skips the expensive longest-path exploration; longestPathSeconds comes back null
  shortestPathOnly?: boolean;
}

export interface IRouteGraphResult {
  shortestPathStopIds: number[] | null;
  shortestPathSeconds: number | null;
  longestPathStopIds: number[] | null;
  longestPathSeconds: number | null;
  longestPathCapped: boolean;
}

// Bounds the exhaustive longest-path exploration so a dense graph can never burn CPU forever;
// when the cap is hit the best value found so far is returned as a lower bound
const LONGEST_PATH_EXPANSION_CAP = 2_000_000;

export function computeRouteGraphPaths(graph: IRouteGraphRequest): IRouteGraphResult {
  const { startStopId, endStopId, segments, virtualEndStopId } = normalizeCyclicGraph(graph);
  const outgoingSegmentsByStopId = new Map<number, IRouteGraphSegment[]>();
  const stopIds = new Set<number>();
  segments.forEach((segment) => {
    stopIds.add(segment.startStopId);
    stopIds.add(segment.endStopId);
    if (!outgoingSegmentsByStopId.has(segment.startStopId)) outgoingSegmentsByStopId.set(segment.startStopId, []);
    outgoingSegmentsByStopId.get(segment.startStopId)!.push(segment);
  });

  if (endStopId == null) return computeDynamicEndPaths(startStopId, outgoingSegmentsByStopId, graph.shortestPathOnly);

  const shortest = findShortestPath(startStopId, endStopId, stopIds, outgoingSegmentsByStopId);
  const longest = shortest && !graph.shortestPathOnly
    ? findLongestPath(startStopId, endStopId, outgoingSegmentsByStopId)
    : { seconds: null, stopIds: null, capped: false };

  const withRealEndStop = (stopIds: number[] | null) =>
    stopIds?.map((stopId) => (stopId === virtualEndStopId ? startStopId : stopId)) ?? null;

  return {
    shortestPathStopIds: withRealEndStop(shortest?.stopIds ?? null),
    shortestPathSeconds: shortest?.seconds ?? null,
    longestPathStopIds: withRealEndStop(longest.stopIds),
    longestPathSeconds: longest.seconds,
    longestPathCapped: longest.capped,
  };
}

// A cyclic route flags the same stop as start and end (or loops back to the start with no
// end flag). Mirroring the backend graph builder, the loop-closing segments are redirected
// to a virtual final stop so paths measure the full loop instead of the trivial zero path.
function normalizeCyclicGraph(graph: IRouteGraphRequest): IRouteGraphRequest & { virtualEndStopId: number | null } {
  const closesLoop = (segment: IRouteGraphSegment) => segment.endStopId === graph.startStopId;
  const isCyclic = graph.endStopId === graph.startStopId || (graph.endStopId == null && graph.segments.some(closesLoop));
  if (!isCyclic) return { ...graph, virtualEndStopId: null };
  const virtualEndStopId = graph.segments.reduce((lowestId, segment) =>
    Math.min(lowestId, segment.startStopId, segment.endStopId), graph.startStopId) - 1;
  return {
    startStopId: graph.startStopId,
    endStopId: virtualEndStopId,
    segments: graph.segments.map((segment) => (closesLoop(segment) ? { ...segment, endStopId: virtualEndStopId } : segment)),
    virtualEndStopId,
  };
}

// No end stop (dynamic end): an expedition may finish at any stop, so the minimum path is
// zero and the maximum is the longest simple path from the start to anywhere in the graph
function computeDynamicEndPaths(
  startStopId: number,
  outgoingSegmentsByStopId: Map<number, IRouteGraphSegment[]>,
  shortestPathOnly?: boolean,
): IRouteGraphResult {
  if (shortestPathOnly) return { shortestPathStopIds: null, shortestPathSeconds: 0, longestPathStopIds: null, longestPathSeconds: null, longestPathCapped: false };
  const longest = findLongestPath(startStopId, null, outgoingSegmentsByStopId);
  return {
    shortestPathStopIds: null,
    shortestPathSeconds: 0,
    longestPathStopIds: longest.stopIds,
    longestPathSeconds: longest.seconds ?? 0,
    longestPathCapped: longest.capped,
  };
}

function findShortestPath(
  startStopId: number,
  endStopId: number,
  stopIds: Set<number>,
  outgoingSegmentsByStopId: Map<number, IRouteGraphSegment[]>,
): { stopIds: number[]; seconds: number } | null {
  const distances = new Map<number, number>([[startStopId, 0]]);
  const previousStopIds = new Map<number, number>();
  const pendingStopIds = new Set<number>(stopIds);
  pendingStopIds.add(startStopId);
  while (pendingStopIds.size > 0) {
    let currentStopId: number | null = null;
    let currentDistance = Infinity;
    pendingStopIds.forEach((stopId) => {
      const distance = distances.get(stopId);
      if (distance != null && distance < currentDistance) {
        currentDistance = distance;
        currentStopId = stopId;
      }
    });
    if (currentStopId == null) break;
    pendingStopIds.delete(currentStopId);
    if (currentStopId === endStopId) break;
    (outgoingSegmentsByStopId.get(currentStopId) ?? []).forEach((segment) => {
      if (!pendingStopIds.has(segment.endStopId)) return;
      const candidateDistance = currentDistance + segment.weightSeconds;
      if (candidateDistance < (distances.get(segment.endStopId) ?? Infinity)) {
        distances.set(segment.endStopId, candidateDistance);
        previousStopIds.set(segment.endStopId, currentStopId!);
      }
    });
  }
  const totalSeconds = distances.get(endStopId);
  if (totalSeconds == null) return null;
  const path: number[] = [];
  let cursorStopId: number | undefined = endStopId;
  while (cursorStopId != null) {
    path.unshift(cursorStopId);
    if (cursorStopId === startStopId) break;
    cursorStopId = previousStopIds.get(cursorStopId);
  }
  return path[0] === startStopId ? { stopIds: path, seconds: totalSeconds } : null;
}

function findLongestPath(
  startStopId: number,
  endStopId: number | null,
  outgoingSegmentsByStopId: Map<number, IRouteGraphSegment[]>,
): { seconds: number | null; stopIds: number[] | null; capped: boolean } {
  let longestSeconds: number | null = endStopId == null ? 0 : null;
  let longestStopIds: number[] | null = endStopId == null ? [startStopId] : null;
  let expansions = 0;
  const onPath = new Set<number>();
  const stack: { stopId: number; accumulatedSeconds: number; nextSegmentIndex: number }[] = [
    { stopId: startStopId, accumulatedSeconds: 0, nextSegmentIndex: 0 },
  ];
  onPath.add(startStopId);
  while (stack.length > 0) {
    const frame = stack[stack.length - 1];
    if (endStopId == null && frame.accumulatedSeconds > (longestSeconds ?? 0)) {
      longestSeconds = frame.accumulatedSeconds;
      longestStopIds = stack.map((pathFrame) => pathFrame.stopId);
    }
    if (frame.stopId === endStopId) {
      if (longestSeconds == null || frame.accumulatedSeconds > longestSeconds) {
        longestSeconds = frame.accumulatedSeconds;
        longestStopIds = stack.map((pathFrame) => pathFrame.stopId);
      }
      onPath.delete(frame.stopId);
      stack.pop();
      continue;
    }
    const outgoingSegments = outgoingSegmentsByStopId.get(frame.stopId) ?? [];
    let advanced = false;
    while (frame.nextSegmentIndex < outgoingSegments.length) {
      const segment = outgoingSegments[frame.nextSegmentIndex++];
      if (onPath.has(segment.endStopId)) continue;
      if (++expansions > LONGEST_PATH_EXPANSION_CAP) {
        return { seconds: longestSeconds, stopIds: longestStopIds, capped: true };
      }
      onPath.add(segment.endStopId);
      stack.push({
        stopId: segment.endStopId,
        accumulatedSeconds: frame.accumulatedSeconds + segment.weightSeconds,
        nextSegmentIndex: 0,
      });
      advanced = true;
      break;
    }
    if (!advanced && frame.nextSegmentIndex >= outgoingSegments.length) {
      onPath.delete(frame.stopId);
      stack.pop();
    }
  }
  return { seconds: longestSeconds, stopIds: longestStopIds, capped: false };
}
